import { Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { AthenaTableGQLType } from '../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { PostgresQueryView } from '../covenantPostgresQuery/PostgresQueryView';
import { useAthenaMonacoEditor } from 'packages/athena/hooks/useAthenaMonacoEditor';
import { AthenaQueryView } from 'packages/athena/ui/AthenaQueryView';
import { AthenaRecurringQueryView } from 'packages/athena/ui/AthenaRecurringQueryView';
import { formatSelectOptions } from 'packages/common-frontend/components/Select';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import {
  isPortfolioCovenantQueryInputType,
  PortfolioCovenantQueryInput,
  PortfolioCovenantQueryInputType,
} from 'packages/settings/ui/AddQueryInputModal';
import { PortfolioCovenantEditTab } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsSidebar';

const getSingleQueryAndIndex = ({
  portfolioCovenant,
  queryInputType,
  queryInputKey,
}: {
  portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  queryInputType: PortfolioCovenantQueryInputType;
  queryInputKey: string;
}): {
  queryIndex: number;
  query: CreditAgreement.Config.AthenaConfig.Query;
} | null => {
  if (queryInputType === 'singleQuery' && portfolioCovenant._config_) {
    const queryIndex = _.findIndex(
      portfolioCovenant._config_.athenaQueries,
      (query) => query.query.name === queryInputKey,
    );

    return {
      queryIndex,
      query: portfolioCovenant._config_.athenaQueries[queryIndex],
    };
  }
  return null;
};
const getMultiQueryAndIndex = ({
  portfolioCovenant,
  queryInputType,
  queryInputKey,
}: {
  portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  queryInputType: PortfolioCovenantQueryInputType;
  queryInputKey: string;
}): {
  queryIndex: number;
  query: CreditAgreement.Config.AthenaConfig.RecurringQuery;
} | null => {
  if (queryInputType === 'multiQuery' && portfolioCovenant._config_) {
    const queryIndex = _.findIndex(
      portfolioCovenant._config_.athenaRecurringQueries,
      (query) => query.name === queryInputKey,
    );
    return {
      queryIndex,
      query: portfolioCovenant._config_.athenaRecurringQueries[queryIndex],
    };
  }
  return null;
};

const getPostgresQueryAndIndex = ({
  portfolioCovenant,
  queryInputType,
  queryInputKey,
}: {
  portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  queryInputType: PortfolioCovenantQueryInputType;
  queryInputKey: string;
}): {
  queryIndex: number;
  query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query;
} | null => {
  if (
    CreditAgreement.Config.CommonPostgresQuery.Query.PostgresQueryTypeIterable.some(
      (item) => item === queryInputType,
    ) &&
    portfolioCovenant._config_
  ) {
    const queryIndex = _.findIndex(
      portfolioCovenant._config_.postgresQueries,
      (query) => query.name === queryInputKey,
    );
    return {
      queryIndex,
      query: portfolioCovenant._config_.postgresQueries[queryIndex],
    };
  }
  return null;
};

type PortfolioCovenantEditInputsContainerProps = {
  organizationId: string;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  triggerDate: Util.FullDateStr;
  portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  selectedQueryInput: PortfolioCovenantQueryInput | null;
  setSelectedQueryInput: React.Dispatch<
    React.SetStateAction<PortfolioCovenantQueryInput | null>
  >;
  setSelectedTab: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >;
  athenaTables: AthenaTableGQLType[];
};
export const PortfolioCovenantEditInputsContainer = ({
  organizationId,
  wipConfig,
  fullPropertyKey: portfolioCovenantFullPropertyKey,
  triggerDate,
  portfolioCovenant,
  selectedQueryInput,
  setSelectedQueryInput,
  setSelectedTab,
  athenaTables,
}: PortfolioCovenantEditInputsContainerProps) => {
  useAthenaMonacoEditor({ athenaTables });

  if (
    !selectedQueryInput ||
    !isPortfolioCovenantQueryInputType(selectedQueryInput.queryInputType) ||
    !portfolioCovenant._config_
  ) {
    return <Text>Select an input.</Text>;
  }

  const { queryInputType, queryInputKey } = selectedQueryInput;
  const presetVariables = [
    {
      name: 'triggerDate',
      value: triggerDate,
      parseNumber: true,
    },
  ];

  if (queryInputType === 'singleQuery') {
    const queryAndIndex = getSingleQueryAndIndex({
      portfolioCovenant,
      queryInputType,
      queryInputKey,
    });

    if (!queryAndIndex) {
      return <Text>Select an input.</Text>;
    }

    const basePropertyKey = `${portfolioCovenantFullPropertyKey}._config_.athenaQueries`;
    const fullPropertyKeyForQuery = `${basePropertyKey}[${queryAndIndex.queryIndex}]`;

    return (
      <AthenaQueryView
        organizationId={organizationId}
        wipConfig={wipConfig}
        basePropertyKey={basePropertyKey}
        fullPropertyKey={fullPropertyKeyForQuery}
        queryView={queryAndIndex.query}
        existingQueries={portfolioCovenant._config_.athenaQueries}
        idx={queryAndIndex.queryIndex}
        inputDateType="triggerDate"
        presetVariables={presetVariables}
        variableSubTitle='"triggerDate" will be an available variable by default that can be used'
        validationVariables={[]}
        setSelectedQueryInput={setSelectedQueryInput}
        setSelectedTab={setSelectedTab}
        selectedInputDate={triggerDate}
      />
    );
  }
  if (queryInputType === 'multiQuery') {
    const queryAndIndex = getMultiQueryAndIndex({
      portfolioCovenant,
      queryInputType,
      queryInputKey,
    });

    if (!queryAndIndex) {
      return <Text>Select an input.</Text>;
    }

    const basePropertyKey = `${portfolioCovenantFullPropertyKey}._config_.athenaRecurringQueries`;
    const fullPropertyKeyForQuery = `${basePropertyKey}[${queryAndIndex.queryIndex}]`;
    return (
      <AthenaRecurringQueryView
        organizationId={organizationId}
        wipConfig={wipConfig}
        basePropertyKey={basePropertyKey}
        fullPropertyKey={fullPropertyKeyForQuery}
        recurringQuery={queryAndIndex.query}
        existingQueries={portfolioCovenant._config_.athenaRecurringQueries}
        globalRecurringDateConfigs={
          portfolioCovenant._config_.globalRecurringDateConfigs
        }
        idx={queryAndIndex.queryIndex}
        inputDateType="triggerDate"
        presetVariables={presetVariables}
        variableSubTitle='"triggerDate" will be an available variable by default that can be used'
        validationVariables={[]}
        setSelectedQueryInput={setSelectedQueryInput}
        setSelectedTab={setSelectedTab}
        selectedInputDate={triggerDate}
      />
    );
  }

  if (
    CreditAgreement.Config.CommonPostgresQuery.Query.PostgresQueryTypeIterable.includes(
      queryInputType,
    )
  ) {
    const queryAndIndex = getPostgresQueryAndIndex({
      portfolioCovenant,
      queryInputType,
      queryInputKey,
    });

    if (!queryAndIndex) {
      return <Text>Select an input.</Text>;
    }

    const basePropertyKey = `${portfolioCovenantFullPropertyKey}._config_.postgresQueries`;
    const fullPropertyKeyForQuery = `${basePropertyKey}[${queryAndIndex.queryIndex}]`;

    return (
      <PostgresQueryView
        postgresQuery={queryAndIndex.query}
        basePropertyKey={basePropertyKey}
        fullPropertyKey={fullPropertyKeyForQuery}
        triggerDate={triggerDate}
        idx={queryAndIndex.queryIndex}
        wipConfig={wipConfig}
        setSelectedQueryInput={setSelectedQueryInput}
        setSelectedTab={setSelectedTab}
        inputDateType="triggerDate"
        presetVariables={presetVariables}
        selectOptions={formatSelectOptions(
          CreditAgreement.Config.PortfolioCovenant.PostgresQuery
            .QueryTypeIterable,
          'title',
        )}
      />
    );
  }
  return <Text>Select an input.</Text>;
};
